import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Define availableModules outside the component (Solution 1)
const availableModules = [
  'Dashboard',
  'Employees',
  'Attendance',
  'User Management',
  'Payroll',
  'Reports',
  'Menu',
  'AttStatus',
  'CustomFields',
  'Settings',
  'AdminPanel',
  'PayComponents',
  'Templates',
  'SalaryTemplates',
  'SalaryDetails',
  'SalarySettings',
  'LeaveMaster',
  'LeaveTypes',
  'Holidays',
  'Shift',
  'Payroll',
  'Layoff',
  'PayrollApproval',
  'Policies',
  'Device',
  'LeaveLedger',
  'PermissionRequest',
  'OnDuty'
];

const PermissionModal = ({ show, onHide, role, roles, permissions, onSuccess, onError }) => {
  const [permissionForm, setPermissionForm] = useState({
    role_id: '',
    selectedModules: [],
    modulePermissions: {}
  });
  const [loading, setLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE;

  useEffect(() => {
    if (role) {
      setPermissionForm(prev => ({
        ...prev,
        role_id: role.role_id
      }));

      // Get existing permissions for this role
      const rolePermissions = permissions.filter(p => p.role_id === role.role_id);
      const selectedModules = rolePermissions.map(p => p.module_name);

      const modulePermissions = rolePermissions.reduce((acc, perm) => {
        acc[perm.module_name] = {
          can_view: Boolean(perm.can_view),
          can_add: Boolean(perm.can_add),
          can_edit: Boolean(perm.can_edit),
          can_delete: Boolean(perm.can_delete),
          can_export: Boolean(perm.can_export),
          can_import: Boolean(perm.can_import),
          can_process: Boolean(perm.can_process),
          can_approve: Boolean(perm.can_approve)
        };
        return acc;
      }, {});

      // Initialize all available modules with default permissions if they don't exist
      availableModules.forEach(module => {
        if (!modulePermissions[module]) {
          modulePermissions[module] = {
            can_view: false,
            can_add: false,
            can_edit: false,
            can_delete: false,
            can_export: false,
            can_import: false,
            can_process: false,
            can_approve: false
          };
        }
      });

      setPermissionForm(prev => ({
        ...prev,
        selectedModules,
        modulePermissions
      }));
    } else {
      setPermissionForm({
        role_id: '',
        selectedModules: [],
        modulePermissions: {}
      });
    }
  }, [role, permissions]); // availableModules removed from dependencies

  const handleModuleSelection = (module, isSelected) => {
    setPermissionForm(prev => {
      const selectedModules = isSelected
        ? [...prev.selectedModules, module]
        : prev.selectedModules.filter(m => m !== module);

      const modulePermissions = { ...prev.modulePermissions };
      if (isSelected && !modulePermissions[module]) {
        modulePermissions[module] = {
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_export: false,
          can_import: false,
          can_process: false,
          can_approve: false
        };
      } else if (!isSelected) {
        delete modulePermissions[module];
      }

      return {
        ...prev,
        selectedModules,
        modulePermissions
      };
    });
  };

  const handlePermissionChange = (module, permissionType, value) => {
    setPermissionForm(prev => ({
      ...prev,
      modulePermissions: {
        ...prev.modulePermissions,
        [module]: {
          ...prev.modulePermissions[module],
          [permissionType]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');

      // Prepare permissions data for all selected modules
      const permissionsData = permissionForm.selectedModules.map(module => ({
        role_id: permissionForm.role_id,
        module_name: module,
        ...permissionForm.modulePermissions[module]
      }));

      const response = await axios.post(`${API_BASE}/update_permissions.php`,
        { permissions: permissionsData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onSuccess();
      } else {
        onError(response.data.error || 'Failed to update permissions');
      }
    } catch (error) {
      onError(error.response?.data?.error || 'Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 shadow">
          <div className="modal-header bg-warning text-dark">
            <h5 className="modal-title">
              <i className="bi bi-shield-check me-2"></i>
              Manage Module Permissions {role && `for ${role.role_name}`}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onHide}
              disabled={loading}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Select Role *</label>
                  <select
                    className="form-select"
                    value={permissionForm.role_id}
                    onChange={(e) => setPermissionForm({ ...permissionForm, role_id: e.target.value })}
                    required
                    disabled={loading || !!role}
                  >
                    <option value="">Choose a role...</option>
                    {roles.filter(r => r.is_active).map(role => (
                      <option key={role.role_id} value={role.role_id}>
                        {role.role_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Module Permissions Table */}
                <div className="col-12">
                  <div className="card">
                    <div className="card-header bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-table me-2"></i>
                        Module Permissions
                      </h6>
                      <small className="text-muted">Select modules and set permissions for the role</small>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        <table className="table table-hover mb-0">
                          <thead className="bg-primary text-white">
                            <tr>
                              <th width="40" style={{ minWidth: '40px' }}>
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={permissionForm.selectedModules.length === availableModules.length}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setPermissionForm(prev => ({
                                          ...prev,
                                          selectedModules: availableModules,
                                          modulePermissions: availableModules.reduce((acc, module) => ({
                                            ...acc,
                                            [module]: {
                                              can_view: false,
                                              can_add: false,
                                              can_edit: false,
                                              can_delete: false,
                                              can_export: false,
                                              can_import: false,
                                              can_process: false,
                                              can_approve: false
                                            }
                                          }), {})
                                        }));
                                      } else {
                                        setPermissionForm(prev => ({
                                          ...prev,
                                          selectedModules: [],
                                          modulePermissions: {}
                                        }));
                                      }
                                    }}
                                    disabled={loading}
                                  />
                                </div>
                              </th>
                              <th>Module</th>
                              <th className="text-center">View</th>
                              <th className="text-center">Add</th>
                              <th className="text-center">Edit</th>
                              <th className="text-center">Delete</th>
                              <th className="text-center">Export</th>
                              <th className="text-center">Import</th>
                              <th className="text-center">Process</th>
                              <th className="text-center">Approve</th>
                            </tr>
                          </thead>
                          <tbody>
                            {availableModules.map(module => (
                              <tr key={module} className={permissionForm.selectedModules.includes(module) ? 'table-active' : ''}>
                                <td style={{ width: '40px' }}>
                                  <div className="form-check m-0">
                                    <input
                                      className="form-check-input m-0"
                                      type="checkbox"
                                      checked={permissionForm.selectedModules.includes(module)}
                                      onChange={(e) => handleModuleSelection(module, e.target.checked)}
                                      disabled={loading}
                                    />
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <i className="bi bi-grid me-2 text-muted"></i>
                                    <span className="fw-semibold">{module}</span>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="form-check form-switch d-inline-block">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      disabled={!permissionForm.selectedModules.includes(module) || loading}
                                      checked={permissionForm.modulePermissions[module]?.can_view || false}
                                      onChange={(e) => handlePermissionChange(module, 'can_view', e.target.checked)}
                                    />
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="form-check form-switch d-inline-block">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      disabled={!permissionForm.selectedModules.includes(module) || loading}
                                      checked={permissionForm.modulePermissions[module]?.can_add || false}
                                      onChange={(e) => handlePermissionChange(module, 'can_add', e.target.checked)}
                                    />
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="form-check form-switch d-inline-block">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      disabled={!permissionForm.selectedModules.includes(module) || loading}
                                      checked={permissionForm.modulePermissions[module]?.can_edit || false}
                                      onChange={(e) => handlePermissionChange(module, 'can_edit', e.target.checked)}
                                    />
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="form-check form-switch d-inline-block">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      disabled={!permissionForm.selectedModules.includes(module) || loading}
                                      checked={permissionForm.modulePermissions[module]?.can_delete || false}
                                      onChange={(e) => handlePermissionChange(module, 'can_delete', e.target.checked)}
                                    />
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="form-check form-switch d-inline-block">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      disabled={!permissionForm.selectedModules.includes(module) || loading}
                                      checked={permissionForm.modulePermissions[module]?.can_export || false}
                                      onChange={(e) => handlePermissionChange(module, 'can_export', e.target.checked)}
                                    />
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="form-check form-switch d-inline-block">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      disabled={!permissionForm.selectedModules.includes(module) || loading}
                                      checked={permissionForm.modulePermissions[module]?.can_import || false}
                                      onChange={(e) => handlePermissionChange(module, 'can_import', e.target.checked)}
                                    />
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="form-check form-switch d-inline-block">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      disabled={!permissionForm.selectedModules.includes(module) || loading}
                                      checked={permissionForm.modulePermissions[module]?.can_process || false}
                                      onChange={(e) => handlePermissionChange(module, 'can_process', e.target.checked)}
                                    />
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="form-check form-switch d-inline-block">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      disabled={!permissionForm.selectedModules.includes(module) || loading}
                                      checked={permissionForm.modulePermissions[module]?.can_approve || false}
                                      onChange={(e) => handlePermissionChange(module, 'can_approve', e.target.checked)}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="card-footer bg-light">
                      <div className="row align-items-center">
                        <div className="col">
                          <small className="text-muted">
                            Selected: {permissionForm.selectedModules.length} of {availableModules.length} modules
                          </small>
                        </div>
                        <div className="col-auto">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              const allModules = [...availableModules];
                              setPermissionForm(prev => ({
                                ...prev,
                                selectedModules: allModules,
                                modulePermissions: allModules.reduce((acc, module) => ({
                                  ...acc,
                                  [module]: {
                                    can_view: true,
                                    can_add: false,
                                    can_edit: false,
                                    can_delete: false,
                                    can_export: false,
                                    can_import: false,
                                    can_process: false,
                                    can_approve: false
                                  }
                                }), {})
                              }));
                            }}
                            disabled={loading}
                          >
                            <i className="bi bi-eye me-1"></i>
                            Set All View Only
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary ms-2"
                            onClick={() => {
                              const allModules = [...availableModules];
                              setPermissionForm(prev => ({
                                ...prev,
                                selectedModules: allModules,
                                modulePermissions: allModules.reduce((acc, module) => ({
                                  ...acc,
                                  [module]: {
                                    can_view: true,
                                    can_add: true,
                                    can_edit: true,
                                    can_delete: true,
                                    can_export: true,
                                    can_import: true,
                                    can_process: true,
                                    can_approve: true
                                  }
                                }), {})
                              }));
                            }}
                            disabled={loading}
                          >
                            <i className="bi bi-check-all me-1"></i>
                            Set All Permissions
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Validation message */}
                {permissionForm.selectedModules.length === 0 && (
                  <div className="col-12">
                    <div className="alert alert-warning py-2">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Please select at least one module to set permissions
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onHide}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-warning"
                disabled={!permissionForm.role_id || permissionForm.selectedModules.length === 0 || loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-shield-check me-2"></i>
                    Save All Permissions
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PermissionModal;
